import { config } from '@psl/functions/config';
import format from 'date-fns/format';
import {
  createContext,
  FC,
  memo,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AppState } from 'react-native';
import { BehaviorSubject } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import {
  isTokenExpired,
  jwtDecode,
} from '../../../../functions/auth/jwtTokenUtil';
import { useAuthToken } from '../../../../functions/auth/useAuthToken';
import useBehaviorSubject from '../../../../functions/hooks/useBehaviorSubject';
import { useCreation } from '../../../../functions/hooks/useCreation';
import { usePersistFn } from '../../../../functions/hooks/usePersistFn';
import { useWatchValue } from '../../../../functions/hooks/usePrevious';
import { makeDebug } from '../../../../utils/makeDebug';
import { SocketEvent, SocketEventArgs } from '../models/socketEvent';
import { isChatDebugEnabled } from '../utils';
import { decodeChatGatewayEventData } from '../utils/decodeChatGatewayEventData';

const kGatewayUrl = config.chatGatewayWsEndpoint;

const debug = makeDebug('chat:gateway', isChatDebugEnabled('gateway'));
const debugIO = makeDebug('chat:gateway:io', isChatDebugEnabled('gatewayIO'));

export type GatewayConnectionStatus =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'disconnected'
  | 'error';

export type EventListenerInfo = {
  name: string;
  listener: any;
  tag: string;
  registered: boolean;
};

export type ChatGatewayEventListener<T extends SocketEvent> = (
  e: SocketEventArgs[T],
) => void;

export type ChatGatewayContextProps = {
  status$: BehaviorSubject<GatewayConnectionStatus>;
  reconnect: () => void;
  addEventListener: (
    name: string,
    listener: (data: any) => void,
    tag?: string,
  ) => () => void;
};

export const ChatGatewayContext = createContext<ChatGatewayContextProps>(
  null as any,
);

export const useChatGatewayContext = () => useContext(ChatGatewayContext);

export const useChatGatewayConnectionStatus = () => {
  const { status$ } = useChatGatewayContext();
  const [status] = useBehaviorSubject(status$);
  return status;
};

export function useChatGatewayEvent<T extends SocketEvent>(
  event: T,
  listener: ChatGatewayEventListener<T>,
  tag: string,
) {
  const { addEventListener } = useChatGatewayContext();

  // stable event handler reference
  const handler = usePersistFn((data: any) => {
    try {
      data = decodeChatGatewayEventData(event, data);
      debug.info('🔥 [%s] received event %s: %O', tag, event, data);
      listener(data);
    } catch (e) {
      debug('❌ [%s] error decode event data: %s', tag, data, e);
    }
  });

  useEffect(() => {
    return addEventListener(event, handler, tag);
  }, [addEventListener, event, handler, tag]);
}

export const ChatGatewayContextProvider: FC<{ children?: ReactNode }> = memo(
  ({ children }) => {
    // keep track of the socket connection status maintained internally.
    const status$ = useCreation(
      () => new BehaviorSubject<GatewayConnectionStatus>('idle'),
      [],
    );

    const [socket] = useState<Socket>(() => {
      return io(kGatewayUrl, {
        autoConnect: false,
        reconnection: true,
        timeout: 120000,
        transports: ['websocket'],
      });
    });

    // keep track of if the gateway connection has been initialized
    // we only want to initialize it once and only update user session
    // accordingly if necessary
    const initialized = useRef(false);

    // dynamically refreshed user authentication token, basically user auth
    // token's expire time is 1 hour by default by firebase authentication
    // firebase authentication will take care of refresh the token automatically
    // when user authentication token is refreshed, we have to reestablish
    // the connection thereafter.
    const [token, refreshToken] = useAuthToken();
    const isRefreshingToken = useRef(false);
    const tokenRef = useRef(token);
    tokenRef.current = token;

    // this function is only expected to be called internally while
    // initializing connection to server.
    const connect = usePersistFn(() => {
      if (!token) {
        debug.warn('🟡 no user auth token available, connect skipped');
        return;
      }
      status$.next('connecting');
      debug.info('⌛ connecting to %s... ', kGatewayUrl);
      socket.connect();
    });

    // this function can be called either internally or externally by user
    // which will refresh the current user's auth token
    const reconnect = usePersistFn(() => {
      if (status$.value === 'connecting' || isRefreshingToken.current) {
        return;
      }

      isRefreshingToken.current = true;

      debug.info('🔑 refreshing user authentication token... ');

      refreshToken()
        .then(res => {
          isRefreshingToken.current = false;
          if (!res) {
            debug.warn('🟡 user session seems expired');
            return;
          }

          // we don't have to call connect manually here because the change
          // of auth token will trigger a reconnection when necessary
          debug.success('🔑 auth token refresh successful');
        })
        .catch(e => {
          isRefreshingToken.current = false;
          debug.error('❌ failed to refresh user auth token: ', e);
          console.error(e);
        });
    });

    // this function is expected to be called internally
    const disconnect = usePersistFn(() => {
      debug.info('⌛ disconnecting... ');
      socket.disconnect();
      status$.next('disconnected');
    });

    const addEventListener: ChatGatewayContextProps['addEventListener'] =
      usePersistFn((name, listener, tag = 'x') => {
        const listenerInfo: EventListenerInfo = {
          name,
          tag,
          listener,
          registered: true,
        };
        socket.on(name as any, listener);
        debug.info(
          '[%s] added event listener %s (%s)',
          tag,
          name,
          socket.connected ? 'connected' : 'before connected',
        );

        return () => {
          debug.warn(
            '[%s] removed event listener %s',
            listenerInfo.tag,
            listenerInfo.name,
          );
          socket.off(name, listener);
        };
      });

    const emitSessionToken = usePersistFn(() => {
      socket.emit('auth.jwt', token);
      debug.success('✅ emitted current user auth token');
    });

    const initialize = usePersistFn(() => {
      debug.info('🚀 initializing socket connection... ');

      socket.on('connect', () => {
        debug.success('🟢 connected to %s 🎉', kGatewayUrl);
        emitSessionToken();
        status$.next('connected');
      });

      socket.on('message', content => {
        debug.info('🔥 received message: %s', content);
      });

      socket.on('disconnect', () => {
        debug.warn('🔴 disconnected');
        status$.next('disconnected');
      });

      socket.io.on('open', () => {
        debugIO('websocket transport open');
      });

      socket.io.on('close', () => {
        debugIO('websocket transport closed');
      });

      socket.io.on('ping', () => {
        debugIO('🎾 websocket transport ping from server');
      });

      socket.io.on('error', err => {
        if (!__DEV__) {
          console.error('chat gateway websocket transport error: ', err);
        }
        debugIO.error('❌ websocket transport error: ', err);
        status$.next('error');
      });

      socket.io.on('packet', packet => {
        debugIO('received packet: %O', packet);
        if (packet.type === 1) {
          debugIO.warn('server closed this connection actively');
          debugIO.info('reconnecting manually in 3s... ');
          setTimeout(() => {
            connect();
          }, 3000);
        }
      });

      socket.io.on('reconnect', attempt => {
        status$.next('connected');
        debugIO(
          'websocket transport reconnected to %s after #%d attempts',
          kGatewayUrl,
          attempt,
        );
      });

      socket.io.on('reconnect_attempt', attempt => {
        if (status$.value !== 'connecting') {
          status$.next('connecting');
        }
        debugIO('websocket transport reconnecting... %d', attempt);
      });

      socket.io.on('reconnect_error', err => {
        if (!__DEV__) {
          console.error('chat gateway websocket reconnect error: ', err);
        }
        status$.next('error');
        debugIO.error('❌ websocket transport reconnect error: ', err);
      });

      socket.io.on('reconnect_failed', () => {
        status$.next('error');
        debugIO.error(
          '❌ websocket transport failed to reconnect to %s',
          kGatewayUrl,
        );
      });
    });

    const updateUserSession = usePersistFn(() => {
      if (!token) return;

      const tokenInfo = jwtDecode(token);
      if (tokenInfo != null) {
        debug.info(
          '🔑 authenticated at %s, token issued at %s, will expire at %s',
          format(tokenInfo.auth_time * 1000, 'MMM d HH:mm:ss'),
          format(tokenInfo.iat * 1000, 'HH:mm:ss'),
          format(tokenInfo.exp * 1000, 'HH:mm:ss'),
        );
      }

      if (socket.connected) {
        emitSessionToken();
      } else {
        debug('🔑 user session pending, will be updated on connected... ');
      }
    });

    // watch changes of the user auth token which will disconnect if
    // user signed out, and only write logs in other cases.
    useWatchValue(token, (prev, curr) => {
      if (curr) {
        if (prev) {
          debug.info('🔑 auth token refreshed: %s', curr);
        } else {
          debug.info('🔑 auth token initialized: %s', token);
        }
      } else {
        debug.warn('🔑 auth token: <none>');
        if (prev) {
          debug.info('💥 disconnect and cleanup on user signed out');
          disconnect();
        }
      }
    });

    useEffect(() => {
      if (!token) {
        // noop when the user is either not logged in or the auth token
        // has not been setup by firebase authentication.
        return;
      }

      if (!initialized.current) {
        // ensure we initialize the socket instance and the underlying
        // io manager/engine only once.
        initialize();
        initialized.current = true;
      }

      if (
        (status$.value !== 'connected' && status$.value !== 'connecting') ||
        !socket.connected ||
        socket.disconnected
      ) {
        // connect or reconnect if the current connection is lost.
        connect();
      }

      // in case user auth token has expired and refreshed thereafter,
      // we don't have to disconnect and then initialize from scratch
      // but only emit the new auth token instead to eliminate unnecessary
      // connection down time.
      updateUserSession();
    }, [
      connect,
      disconnect,
      initialize,
      socket,
      status$,
      token,
      updateUserSession,
    ]);

    // -----------------------------------------------------------------
    // handle connection by responding to app state changes
    // -----------------------------------------------------------------
    const appState = useRef(AppState.currentState);
    useEffect(() => {
      const listener = AppState.addEventListener('change', nextAppState => {
        if (appState.current === 'background' && nextAppState === 'active') {
          debug.info(
            '🌈 app becomes foreground, connection status: %s, token expired: %s',
            status$.value,
            tokenRef.current ? isTokenExpired(tokenRef.current) : 'n/a',
          );
          // we wait a little bit to allow the socket connection status to
          // be stable after user switch from background to foreground.
          // in case of auth token expiration, duration the wait period,
          // firebase authentication will try to refresh the token automatically
          // so that the connection can be reestablished thereafter and we
          // don't have to do that manually again here.
          setTimeout(() => {
            if (socket.connected) {
              debug.success('✅ stays connected on app becomes foreground');
            } else {
              // connection might be lost after a long sleep in background
              // in which time, the user's previous auth token might have
              // been expired, so we have to refresh the user auth token
              // and try to reconnect thereafter.
              debug.warn(
                '🔥 connection lost while in background, reconnecting...',
              );
              reconnect();
            }
          }, 3000 /* this will be good balanced delay */);
        }
        appState.current = nextAppState;
      });

      debug('added app state change listener');

      return () => {
        debug('remove app state change listener');
        listener.remove();
      };
    }, [reconnect, socket, status$]);

    useEffect(
      () => () => {
        debug.info('💥 cleanup and dispose');
        socket.disconnect();
        socket.removeAllListeners();
        socket.io.removeAllListeners();
        socket.io.engine?.close();
      },
      [socket],
    );

    const value = useMemo<ChatGatewayContextProps>(
      () => ({
        status$,
        addEventListener,
        reconnect,
      }),
      [addEventListener, reconnect, status$],
    );

    return (
      <ChatGatewayContext.Provider value={value}>
        {children}
      </ChatGatewayContext.Provider>
    );
  },
);
